import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import PhoneInput from "react-phone-number-input";
import { toast } from "react-toastify";

import http from "../../../helpers/http";
import { doObjToFormData } from "../../../helpers/helpers";
import { TOAST_SETTINGS } from "../../../utils/siteSettings";
import SubmitButton from "../../common/SubmitButton";
import ShowPasswordIcon from "../../common/ShowPasswordIcon";
import Captcha from "../../common/captcha";
import Text from "../../common/Text";
import SmsConsentFields from "../signup/SmsConsentFields";
import TermsPopup from "../checkout/termsPopup";
import { defaultPlanId, eventCheckoutUrl } from "./eventPlans";
import styles from "../../../CSS/sections/EventSignup.module.css";

// Same values as signup/Company.jsx.
const TEAM_SIZES = [
  { value: 1, label: "1-10" },
  { value: 2, label: "10-15" },
  { value: 3, label: "15-25" },
  { value: 4, label: "25-50" },
  { value: 5, label: "50+" },
];

// "card" also covers Apple Pay and Google Pay: Stripe's hosted page offers the
// wallets automatically for card sessions (see checkout/Form.jsx).
const PAYMENT_METHODS = [
  { value: "card", label: "Card, Apple Pay or Google Pay" },
  { value: "ach", label: "Bank account (ACH)" },
];

const TERMS_FAILED_MSG =
  "We couldn't load the Terms & Conditions. Please refresh the page to continue.";

const NAME_PATTERN = {
  value: /^[a-zA-Z][a-zA-Z ]*$/,
  message: "Invalid value",
};

const EventSignupForm = ({
  plans,
  planSlug,
  promoCode,
  isLoggedIn,
  memberEmail,
  industries,
  primary_interests,
  terms,
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState(() =>
    defaultPlanId(plans, planSlug)
  );
  const selectedPlan =
    plans.find((plan) => plan.encoded_id === selectedPlanId) || null;
  const [paymentMethod, setPaymentMethod] = useState("card");

  // Set once create-account succeeds, so a retry never tries to create the
  // same account again (the email would already be taken).
  const [createdEmail, setCreatedEmail] = useState(null);
  const hasAccount = isLoggedIn || !!createdEmail;

  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  // Bumped to remount the CAPTCHA, so a retry needs a fresh token.
  const [captchaKey, setCaptchaKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsPopup, setTermsPopup] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const pendingValuesRef = useRef(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm();

  // Same as helpers.logout(), but stays on this page (code included) so the
  // next attendee gets the account form. A booth device may be left logged
  // in, and the trial would otherwise start on that account.
  const logOut = () => {
    localStorage.removeItem("authToken");
    window.location.reload();
  };

  // Anything this page can't finish goes to the normal checkout, which
  // already has the plan and the code. The account exists by then.
  const fallBackToCheckout = (message) => {
    toast.info(
      message
        ? `${message} Finish on the next screen.`
        : "Your account is ready. Finish on the next screen.",
      TOAST_SETTINGS
    );
    setTimeout(
      () => window.location.replace(eventCheckoutUrl(selectedPlan, promoCode)),
      1500
    );
  };

  const createAccount = async (values) => {
    try {
      const { data } = await http.post(
        "create-account",
        doObjToFormData({
          fname: values.fname,
          lname: values.lname,
          email: values.email,
          mem_phone: values.mem_phone,
          transactional_sms_consent: values.transactional_sms_consent,
          marketing_sms_consent: values.marketing_sms_consent,
          property_recommendation_updates: values.property_recommendation_updates,
          password: values.password,
          confirm_password: values.password,
          mem_business: values.mem_business,
          team_size: values.team_size,
          industry: values.industry,
          primary_interest: values.primary_interest,
          type: "web",
          captcha_token: captchaToken,
        })
      );
      if (data?.status && data?.authToken) {
        // Same as the signup reducer: the token in localStorage is the login.
        localStorage.setItem("authToken", data.authToken);
        setCreatedEmail(values.email);
        return true;
      }
      toast.error(
        <Text string={data?.msg || "Could not create your account."} parse={true} />,
        TOAST_SETTINGS
      );
    } catch (error) {
      toast.error(
        "Could not create your account. Please try again.",
        TOAST_SETTINGS
      );
    }
    return false;
  };

  // Same request as the checkout's hosted free trial (checkout/Form.jsx
  // requestCheckoutLink). Stripe's page takes the payment, blocks prepaid
  // cards for free trials, and the checkout.session.completed webhook
  // provisions the subscription.
  const openStripeCheckout = async () => {
    try {
      const { data } = await http.post("create-checkout-link", {
        plan_id: selectedPlan.encoded_id,
        payment_method_type: paymentMethod,
        coupon_code: promoCode,
        host: window.location.hostname,
      });
      if (data?.status && data?.url) {
        window.location.assign(data.url);
        return;
      }
      fallBackToCheckout(data?.msg);
    } catch (error) {
      fallBackToCheckout(error?.response?.data?.msg);
    }
  };

  const startTrial = async (values) => {
    setIsSubmitting(true);
    if (!hasAccount && !(await createAccount(values))) {
      setCaptchaToken(null);
      setCaptchaKey((key) => key + 1);
      setIsSubmitting(false);
      return;
    }
    // Keep the spinner on: the page is about to leave for Stripe or checkout.
    await openStripeCheckout();
  };

  const onSubmit = (values) => {
    if (!selectedPlan) {
      toast.error("Please choose a plan.", TOAST_SETTINGS);
      return;
    }
    if (!hasAccount && !captchaToken) {
      toast.error("Please verify the CAPTCHA", TOAST_SETTINGS);
      return;
    }
    // Never ask anyone to accept terms we could not show them.
    if (terms?.failed) {
      toast.error(TERMS_FAILED_MSG, TOAST_SETTINGS);
      return;
    }
    if (!isTermsAccepted) {
      pendingValuesRef.current = values;
      setTermsPopup(true);
      return;
    }
    startTrial(values);
  };

  // Back from Stripe's page: Safari (iOS especially) can restore this page from
  // the back/forward cache with the spinner still on and the form disabled.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) setIsSubmitting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Ticking the box in the terms popup continues the submit that opened it.
  useEffect(() => {
    if (!isTermsAccepted) return;
    setTermsPopup(false);
    const values = pendingValuesRef.current;
    pendingValuesRef.current = null;
    if (values) startTrial(values);
  }, [isTermsAccepted]);

  const fieldError = (name) => (
    <ErrorMessage
      errors={errors}
      name={name}
      render={({ message }) => <p className="validation_error"> {message}</p>}
    />
  );

  return (
    <fieldset>
      <h4>Start your free trial</h4>
      <p>
        {plans.length > 1 ? "Pick a plan and create your account" : "Create your account"}
        , then pay securely with Stripe.
      </p>
      {promoCode && (
        <div className={styles.codeBadge}>
          Free-trial code <strong>{promoCode}</strong> is applied automatically.
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <h6 className={styles.sectionLabel}>
          {plans.length > 1 ? "1. Choose your plan" : "1. Your plan"}
        </h6>
        <div className={styles.plans} role="radiogroup">
          {plans.length === 0 && (
            <p>Plans could not be loaded. Please refresh the page.</p>
          )}
          {plans.map((plan) => {
            const selected = plan.encoded_id === selectedPlanId;
            const users = parseInt(plan.user_count, 10);
            return (
              <label
                key={plan.encoded_id}
                className={`${styles.plan} ${selected ? styles.planSelected : ""}`}
              >
                <input
                  type="radio"
                  name="event_plan"
                  value={plan.encoded_id}
                  checked={selected}
                  disabled={isSubmitting}
                  onChange={() => setSelectedPlanId(plan.encoded_id)}
                />
                <span className={styles.planBody}>
                  <span className={styles.planName}>{plan.plan_name}</span>
                  <span className={styles.planMeta}>
                    {parseInt(plan.clicks_count, 10)} credits · {users} user
                    {users > 1 ? "s" : ""}
                  </span>
                </span>
                <span className={styles.planPrice}>
                  ${parseInt(plan.price, 10)}
                  <sub>/mo</sub>
                </span>
              </label>
            );
          })}
        </div>

        <h6 className={styles.sectionLabel}>2. Your account</h6>
        {hasAccount ? (
          <p className={styles.signedIn}>
            {createdEmail ? (
              `Account created for ${createdEmail}.`
            ) : (
              <>
                Signed in as{" "}
                <strong>{memberEmail || "an existing account"}</strong>. Not
                you?{" "}
                <button
                  type="button"
                  className={styles.linkBtn}
                  disabled={isSubmitting}
                  onClick={logOut}
                >
                  Log out
                </button>
              </>
            )}
          </p>
        ) : (
          <>
            <div className="blk_form_flex">
              <div className="blk_form">
                <h6>First Name</h6>
                <div className="form_blk">
                  <input
                    type="text"
                    className="input"
                    placeholder="First name"
                    autoComplete="given-name"
                    {...register("fname", {
                      pattern: NAME_PATTERN,
                      required: "First Name is required.",
                      minLength: {
                        value: 2,
                        message: "First Name should contains at-least 2 letters.",
                      },
                    })}
                  />
                  {fieldError("fname")}
                </div>
              </div>
              <div className="blk_form">
                <h6>Last Name</h6>
                <div className="form_blk">
                  <input
                    type="text"
                    className="input"
                    placeholder="Last name"
                    autoComplete="family-name"
                    {...register("lname", {
                      pattern: NAME_PATTERN,
                      required: "Last Name is required.",
                      minLength: {
                        value: 2,
                        message: "Last Name should contains at-least 2 letters.",
                      },
                    })}
                  />
                  {fieldError("lname")}
                </div>
              </div>
            </div>
            <div className="blk_form">
              <h6>Email</h6>
              <div className="form_blk">
                <input
                  type="email"
                  className="input"
                  placeholder="you@company.com"
                  autoComplete="email"
                  {...register("email", {
                    required: "Email is required.",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Please enter a valid email",
                    },
                  })}
                />
                {fieldError("email")}
              </div>
            </div>
            <div className="blk_form">
              <h6>Phone Number</h6>
              <div className="form_blk">
                <PhoneInput
                  defaultCountry="US"
                  className="input"
                  autoComplete="tel"
                  {...register("mem_phone", { required: "Phone is required" })}
                />
                {fieldError("mem_phone")}
              </div>
            </div>
            <SmsConsentFields register={register} />
            <div className="blk_form">
              <h6>Password</h6>
              <div className="form_blk pass_blk">
                <input
                  type={showPassword ? "text" : "password"}
                  className="input"
                  placeholder="Create a password"
                  autoComplete="new-password"
                  {...register("password", {
                    required: "Password is required.",
                    pattern: {
                      value:
                        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[A-Za-z0-9\!\"\#\$\%\&\'\(\)\*\+\,\-\.\/\:\;\<\>\=\?\@\[\]\{\}\\\\\^\_\`\~]{8,}$/,
                      message:
                        "The password should contain at least 8 characters, that contain at least one lowercase letter, one uppercase letter and one numeric digit.",
                    },
                  })}
                />
                <ShowPasswordIcon
                  showPassword={showPassword}
                  handleShowPassword={() => setShowPassword(!showPassword)}
                />
                {fieldError("password")}
              </div>
            </div>
            <div className="blk_form">
              <h6>Company Name</h6>
              <div className="form_blk">
                <input
                  type="text"
                  className="input"
                  placeholder="Company name"
                  autoComplete="organization"
                  {...register("mem_business", { required: "Required." })}
                />
                {fieldError("mem_business")}
              </div>
            </div>
            <div className="blk_form">
              <h6>Team size</h6>
              <div className="form_blk">
                <select
                  className={`input ${styles.select}`}
                  defaultValue=""
                  {...register("team_size", { required: "Required" })}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {TEAM_SIZES.map((size) => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
                {fieldError("team_size")}
              </div>
            </div>
            <div className="blk_form">
              <h6>Industry</h6>
              <div className="form_blk">
                <select
                  className={`input ${styles.select}`}
                  defaultValue=""
                  {...register("industry", { required: "Required" })}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {(industries || []).map((industry) => (
                    <option key={industry.value} value={industry.value}>
                      {industry.label}
                    </option>
                  ))}
                </select>
                {fieldError("industry")}
              </div>
            </div>
            <div className="blk_form">
              <h6>Primary interest</h6>
              <div className="form_blk">
                <select
                  className={`input ${styles.select}`}
                  defaultValue=""
                  {...register("primary_interest", { required: "Required" })}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {(primary_interests || []).map((interest) => (
                    <option key={interest.value} value={interest.value}>
                      {interest.label}
                    </option>
                  ))}
                </select>
                {fieldError("primary_interest")}
              </div>
            </div>
          </>
        )}

        <h6 className={styles.sectionLabel}>3. Payment</h6>
        <div className={styles.plans} role="radiogroup">
          {PAYMENT_METHODS.map((method) => {
            const selected = method.value === paymentMethod;
            return (
              <label
                key={method.value}
                className={`${styles.plan} ${selected ? styles.planSelected : ""}`}
              >
                <input
                  type="radio"
                  name="event_payment_method"
                  value={method.value}
                  checked={selected}
                  disabled={isSubmitting}
                  onChange={() => setPaymentMethod(method.value)}
                />
                <span className={styles.planBody}>
                  <span className={styles.planName}>{method.label}</span>
                </span>
              </label>
            );
          })}
        </div>
        <p className={styles.cardNote}>
          You'll pay on Stripe's secure page next.
          {paymentMethod === "card" &&
            " Prepaid cards can't be used for free trials."}
        </p>

        {!hasAccount && (
          <div className="blk_form">
            <Captcha
              key={captchaKey}
              onVerify={(token) => setCaptchaToken(token)}
            />
          </div>
        )}
        {terms?.failed && (
          <p className="validation_error" role="alert">
            {TERMS_FAILED_MSG}
          </p>
        )}
        <div className="btn_blk">
          <SubmitButton
            classes="site_btn"
            isFormProcessing={isSubmitting}
            disabled={!selectedPlan}
            button_text="Continue to secure payment"
          />
        </div>
      </form>
      {!hasAccount && (
        <div className="dont_acc">
          <p>
            Already have an account? <Link to="/login">Log in</Link>, then scan
            the code again.
          </p>
        </div>
      )}
      {termsPopup && (
        <TermsPopup
          terms_conditions={terms?.terms_conditions}
          subscription_agreement={terms?.subscription_agreement}
          setTermsPopup={setTermsPopup}
          setIsTermsAccepted={setIsTermsAccepted}
          isTermsAccepted={isTermsAccepted}
        />
      )}
    </fieldset>
  );
};

export default EventSignupForm;
